import Component from '@glimmer/component';

import { concat, array, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import LinksTo from 'ember-route-state/components/links-to.gjs';

import CButton from 'rise-ui/components/c-button.gjs';
import CColor from 'rise-ui/components/c-color.gjs';
import CIcon from 'rise-ui/components/c-icon.gjs';
import DateRangeDisplay from 'rise-ui/components/date-range-display.gjs';
import GraphScroll from 'rise-ui/components/graph-scroll.gjs';
import GraphView from 'rise-ui/components/graph-view.gjs';
import Sticky from 'rise-ui/components/sticky.gjs';
import Plot from 'rise-ui/components/time-series/output/line/plot.gjs';
import Download from 'rise-ui/components/time-series/output/line/download.gjs';
import LegendTooltip from 'rise-ui/components/time-series/output/line/legend-tooltip.gjs';
import ViewApiRequest from 'rise-ui/components/view-api-request.gjs';
import config from 'rise-ui/config/config.js';
import gc from 'rise-ui/config/time-series/graphs.js';
import and from 'rise-ui/helpers/and.js';
import availableDates from 'rise-ui/helpers/available-dates.js';
import concatClasses from 'rise-ui/helpers/concat-classes.js';
import classMod from 'rise-ui/helpers/class-mod.js';
import classModConfig from 'rise-ui/helpers/class-mod-config.js';
import cssVar from 'rise-ui/helpers/css-var.js';
import dateNow from 'rise-ui/helpers/date-now.js';
import datePartlyUnbound from 'rise-ui/helpers/date-partly-unbound.js';
import dateTime from 'rise-ui/helpers/date-time.js';
import dateUnbound from 'rise-ui/helpers/date-unbound.js';
import filter from 'rise-ui/helpers/filter.js';
import find from 'rise-ui/helpers/find.js';
import fnx from 'rise-ui/helpers/fnx.js';
import graphDownload from 'rise-ui/helpers/graph-download.js';
import isUnset from 'rise-ui/helpers/is-unset.js';
import join from 'rise-ui/helpers/join.js';
import linkToItem from 'rise-ui/helpers/link-to-item.js';
import localeNum from 'rise-ui/helpers/locale-num.js';
import metaEvery from 'rise-ui/helpers/meta-every.js';
import metaGet from 'rise-ui/helpers/meta-get.js';
import metaMap from 'rise-ui/helpers/meta-map.js';
import metaToggle from 'rise-ui/helpers/meta-toggle.js';
import metaToggleAll from 'rise-ui/helpers/meta-toggle-all.js';
import minutes from 'rise-ui/helpers/minutes.js';
import not from 'rise-ui/helpers/not.js';
import oneString from 'rise-ui/helpers/one-string.js';
import or0 from 'rise-ui/helpers/or.js';
import parameterTimestep from 'rise-ui/helpers/parameter-timestep.js';
import parameterTransformation from 'rise-ui/helpers/parameter-transformation.js';
import parameterUnit from 'rise-ui/helpers/parameter-unit.js';
import qpDateObject from 'rise-ui/helpers/qp-date-object.js';
import qpDateUnfilled from 'rise-ui/helpers/qp-date-unfilled.js';
import scaleHasTimeFn from 'rise-ui/helpers/scale-has-time-fn.js';
import string from 'rise-ui/helpers/string.js';
import timeSeriesDate from 'rise-ui/helpers/time-series-date.js';
import timeSeriesDates from 'rise-ui/helpers/time-series-dates.js';
import titleCase from 'rise-ui/helpers/title-case.js';
import value from 'rise-ui/helpers/value.js';
import sticky from 'rise-ui/modifiers/sticky.js';
import { contrastingColors } from 'rise-ui/utils/color.js';
import { diffDates, formatDate, offsetDate, parseDate } from 'rise-ui/utils/date.js';
import { greatestDate, leastDate, or, readOnly } from 'rise-ui/utils/object/computed.js';
import { strOrNumEq } from 'rise-ui/utils/string.js';

const periodFormats = [
  [ gc.tickYearFormatAt,      'tick-year' ],
  [ gc.tickYearMonthFormatAt, 'tick-year-month' ],
  [ gc.tickDateFormatAt,      'tick-date' ],
];

const defaultTickFormat = 'tick-date-time';

export default class TimeSeriesOutputLineComponent extends Component {
  @service('debug/time-series-output') debugOutput;

  @readOnly('args.results') results;
  @readOnly('results.loader') loader;
  @readOnly('results.loading') isLoading;
  @readOnly('loader.complete') isLoaded;
  get isEmpty() {
    const { isLoaded, loader: { loadedItems } = {} } = this; // Consume
    return isLoaded && !loadedItems;
  }

  @readOnly('results.meta') meta;
  @readOnly('meta.modelRunMembers') modelRunMembers;

  @readOnly('results.results') data;

  @readOnly('results.output') output;
  @or('output.item', 'output') item;
  @or('output.catalogRecord.location', 'output.item.catalogRecord.location') location;
  @readOnly('output.title') title;
  @readOnly('output.description') description;
  @readOnly('output.item') isModeled;

  @readOnly('item.parameter.parameterName') parameterName;
  @readOnly('item.parameter.parameterUnit') parameterUnit;
  @readOnly('item.parameter.parameterTimestep') parameterTimestep;

  @readOnly('results.dates') dates;
  @readOnly('dates.from') queriedFrom;
  @readOnly('dates.to') queriedTo;

  // Offset end date by one day to show results for later in that day
  get effectiveQueriedTo() {
    let { queriedTo } = this;
    if (queriedTo) {
      queriedTo = formatDate(offsetDate(parseDate(queriedTo, 'url'), 1, 'day'), 'url');
    }
    return queriedTo;
  }

  @or('output.temporalStartDate', 'output.item.temporalStartDate') availableFrom;
  @or('output.temporalEndDate', 'output.item.temporalEndDate') availableTo;
  @greatestDate('{queriedFrom,availableFrom}') actualFrom;
  @leastDate('{queriedTo,availableTo}') actualTo;
  @greatestDate('{actualFrom,_displayFrom}') displayFrom;
  @leastDate('{actualTo,_displayTo}') displayTo;
  @tracked _displayFrom;
  @tracked _displayTo;
  @tracked boundFrom;
  @tracked boundTo;
  @tracked nDataKeys;
  @tracked isDateRangeConstrained;
  @tracked hasNullData;
  @tracked windowStart;
  @tracked windowEnd;
  @tracked tooltips;
  @tracked secondaryTooltips;
  @tracked graphScrollMarginLeft;
  @tracked graphScrollHandleWidth;

  graph = {};

  /**
   * Gives whole number of periods of given period in given domain of timescale
   * @param {[]} domain
   * @param {string} period 'years', 'months', 'days', etc...
   * @return {number}
   */
  @action getPeriods(domain, period) {
    return Math.abs(diffDates(parseDate(domain[0]), parseDate(domain[1]), period));
  }

  @action tickArguments(size) {
    return Math.max(3, Math.floor(size / 64));
  }

  @action tickMaxFormat(value) {
    return formatDate(parseDate(value), defaultTickFormat);
  }

  @action tickFormat(scale) {
    const domain = scale.domain();
    if (domain[0] instanceof Date) {
      let tickFormat;
      const periods = {};
      const matched = periodFormats.some(([ at, format ]) => {
        if (!format || !Array.isArray(at) || at.length < 2) {
          return;
        }
        const [ atPeriods, atPeriod ] = at;
        const domainPeriods = periods[atPeriod] = periods[atPeriod]
          || this.getPeriods(domain, atPeriod);
        if (domainPeriods >= atPeriods) {
          tickFormat = format;
          return true;
        }
      });
      if (!matched) {
        tickFormat = defaultTickFormat;
      }
      return value => formatDate(parseDate(value), tickFormat);
    }
  }

  @or('modelRunMembers.length', 'nDataKeys') dataKeyCount;

  // Setup nice contrasting line color function based on number of graph lines
  @cached get dataKeyColors() {
    return contrastingColors(this.dataKeyCount, gc.dataKeyColors);
  }

  @cached get dataKeyIndex() {
    const { modelRunMembers } = this; // Consume
    return dataKey => {
      return (modelRunMembers || []).findIndex(mrm => strOrNumEq(mrm.id, dataKey));
    };
  }

  get dataKeyLabels() {
    const { modelRunMembers } = this; // Consume
    return (dataKey, index) => modelRunMembers?.[index]?.description;
  }

  get hasMultipleDataKeys() {
    return this.dataKeyCount > 1;
  }

  @action onTooltipChange(tooltips, secondaryTooltips) {
    Object.assign(this, { tooltips, secondaryTooltips });
  }

  @action onWindowAnimate(windowStart, windowEnd) {
    Object.assign(this, { windowStart, windowEnd });
  }

  @action onGraphRectChange(rect) {
    this.graphScrollMarginLeft = rect.left;
  }

  @action onGraphScrollHandleSizeChange(size) {
    this.graphScrollHandleWidth = size.width;
  }

  @action onDomain(domain, datumCount, dataKeys, actualDomain, hasNulls) {
    const [ displayFrom, displayTo ] = domain.x;
    const [ boundFrom, boundTo ] = actualDomain.x
    const { dateRangeConstrainedGraceDays } = config.timeSeries ?? {};
    const isDateRangeConstrained = this.isLoaded
      && (diffDates(boundFrom, displayFrom, 'days') > dateRangeConstrainedGraceDays
        || diffDates(displayTo, boundTo, 'days') > dateRangeConstrainedGraceDays);
    Object.assign(this, {
      _displayFrom: displayFrom,
      _displayTo: displayTo,
      boundFrom,
      boundTo,
      nDataKeys: dataKeys.length,
      isDateRangeConstrained,
      hasNullData: hasNulls,
    });
  }

  @readOnly('output.item.isActuallyModeled') isActuallyModeled;
  @readOnly('output.item.hasProfile') hasProfile;

  <template>
    {{#let
      (availableDates this.output)
      (cssVar "--color-reclamation-link-color")
      as | availableDates lineColor |
    ~}}
    {{#let
      (qpDateUnfilled this.dates)
      (dateUnbound availableDates.from availableDates.to this.dates)
      (datePartlyUnbound availableDates.from availableDates.to this.dates skip="to")
      (datePartlyUnbound availableDates.from availableDates.to this.dates skip="from")
      (timeSeriesDates this.displayFrom this.displayTo)

      (concat
        this.location.name " "
        (if this.isModeled (concat this.output.title " "))
        (if this.isActuallyModeled "Modeled" "Observed") " "
        (parameterTimestep this.parameterTimestep) " "
        (parameterTransformation this.item.parameter.parameterTransformation) " "
        this.parameterName " " (parameterUnit this.parameterUnit) " "
        "(" (timeSeriesDates this.actualFrom this.actualTo) ")"
      )
      (concat this.output.title " (" (timeSeriesDates this.displayFrom this.displayTo) ")")
      (concat
        this.location.name
        (if this.isModeled (concat " - " this.output.title))
      )
      (join (filter (array
        (titleCase (oneString this.isActuallyModeled "item-modeled-value" true "item-observed-value"))
        (parameterTimestep this.parameterTimestep)
        (parameterTransformation this.item.parameter.parameterTransformation)
        this.parameterName
        (concat "(" (parameterUnit this.parameterUnit) ")")
      )) " ")

      (metaMap this.modelRunMembers visible=true)
      (classModConfig "c-time-series-output")
      as |
        isDateRangeUnfilled isDateRangeUnbound isFromPartlyUnbound isToPartlyUnbound displayDates
        queriedTitle displayTitle titleLine1 titleLine2
        mrmMeta classModCfg
      |
    ~}}
    {{#let
      (array
        (array titleLine1 14)
        (array titleLine2 14)
        (array (concat displayDates (unless this.isLoaded " (Incomplete)")) 12)
      )
      (array
        (array
          (concat
            "Downloaded from data.usbr.gov on "
            (timeSeriesDate (dateNow interval=(minutes 1)))
          )
          8
        )
      )
      as | graphHeadings graphFooters |
    ~}}
    {{#let
      (or0 isFromPartlyUnbound isToPartlyUnbound)
      (and isFromPartlyUnbound isToPartlyUnbound) this.output.$hasInvalidDates
      (graphDownload
        title=(concat
          "Download Image of Displayed Graph"
          (if this.data.length (unless this.isLoaded " (Incomplete)") " (No Graph)")
        )
        disabled=(not this.data.length)
        headings=graphHeadings
        footers=graphFooters
        filename=(concat displayTitle (unless this.isLoaded " (Incomplete)"))
        data=this.data
        dataKey="modelRunMemberId"
        dataKeyColors=this.dataKeyColors
        dataKeyLabels=this.dataKeyLabels
        dataKeyIndex=this.dataKeyIndex
        dataKeyCount=this.dataKeyCount
        yLabel=(concat this.parameterName (if this.parameterUnit (concat " (" this.parameterUnit ")")))
        yKey="value"
        xLabel=(scaleHasTimeFn "Date and Time (UTC)" "Date")
        xKey="dateTime"
        xTickAngle=-25
        tickFormat=this.tickFormat
        tickMaxFormat=this.tickMaxFormat
        xLower=(or0 this.windowStart (qpDateObject this.queriedFrom))
        xUpper=(or0 this.windowEnd (qpDateObject this.effectiveQueriedTo))
        fontSize=12
        backgroundColor="white"
        lineAlpha=(cssVar "--c-graph-view__line___opacity")
        lineColor=lineColor
        legendRight=true
      )
      as | isDateRangePartlyWide isDateRangeWide isDateRangeInvalid graphDownload |
    ~}}
      <div class={{classMod config=classModCfg suffix="__section"}}
        data-qa-output={{if this.isActuallyModeled "modeled" "observed"}}
        data-qa-output-id={{this.output.id}}
      >

        {{#if false}}
        <Sticky @name="meta">
          <div class="c-time-series-output">
            <div class="c-buttons">
              {{#unless this.isLoaded}}
                <Plot
                  @isLoading={{this.isLoading}}
                  @isLoaded={{this.isLoaded}}
                  @isEmpty={{this.isEmpty}}
                  @loader={{this.loader}}
                  @disabled={{or0 isDateRangeInvalid this.isLoaded}}
                  @onClickPlotButton={{fnx @onClickPlotButton @results noMoreArgs=true}}
                />
              {{/unless}}
              <Download
                @disabled={{or0 isDateRangeInvalid (not (or0 this.output.id this.data.length))}}
                @filename={{queriedTitle}}
                @outputId={{this.output.id}}
                @isModeled={{this.isModeled}}
                @imageDisabled={{not this.data.length}}
                @onSelectImage={{graphDownload.onSelection}}
              />
            </div>
            <h4 class={{concatClasses "c-item-title" (classMod config=classModCfg suffix="__section-title")}}>
              {{titleLine1}}
            </h4>
            <h5 class={{concatClasses "c-item-subtitle" (classMod config=classModCfg suffix="__section-subtitle")}}>
              {{titleLine2}}
            </h5>
          </div>
        </Sticky>
        {{/if}}

        <h4 class={{concatClasses
            "c-item-title"
            (classMod config=classModCfg suffix="__section-title")
          }}
          {{sticky activate="meta"}}
        >
          {{titleLine1}}
        </h4>
        <h5 class={{concatClasses
            "c-item-subtitle"
            (classMod config=classModCfg suffix="__section-subtitle")
          }}>
          {{titleLine2}}
        </h5>

        <LinksTo @routeOpen={{linkToItem this.output}}>
          View {{titleCase (string "catalog-item")}} Details for this Time Series
        </LinksTo>
        <div class={{classMod config=classModCfg suffix="__section-dates"}}>
          <div class={{classMod config=classModCfg suffix="__section-date-range"}}>
            <h6>Queried Date Range</h6>
            <DateRangeDisplay
              @fromDate={{this.queriedFrom}}
              @toDate={{this.queriedTo}}
            />
          </div>
          {{#if this.data.length}}
            <div class={{classMod config=classModCfg suffix="__section-date-range"}}>
              <h6>Displayed Date Range</h6>
              <DateRangeDisplay
                @fromDate={{this.displayFrom}}
                @toDate={{this.displayTo}}
              />
            </div>
          {{/if}}
        </div>
        {{#if (and (not isDateRangeUnbound) (or0
          isDateRangePartlyWide this.isDateRangeConstrained this.hasNullData
        ))}}
          <div class={{classMod config=classModCfg suffix="__section-plot-warnings"}}>
            {{#if isDateRangeWide}}
              <div class="c-warning qa-warn-dates qa-wide-dates">
                Start and end date are outside the available date range
              </div>
            {{else if isFromPartlyUnbound}}
              <div class="c-warning qa-warn-dates qa-early-dates">
                Start date is outside available date range
              </div>
            {{else if isToPartlyUnbound}}
              <div class="c-warning qa-warn-dates qa-late-dates">
                End date is outside available date range
              </div>
            {{/if}}
            {{#if this.isDateRangeConstrained}}
              <div class="c-warning qa-warn-dates qa-gapped-dates">
                Data only available for part of selected date range
              </div>
            {{/if}}
            {{#if this.hasNullData}}
              <div class="c-warning qa-warn qa-null-values">
                Null values exist within selected date range
              </div>
            {{/if}}
          </div>
        {{/if}}
        <div class={{classMod config=classModCfg suffix="__section-downloads"}}>
          <div class={{classMod config=classModCfg suffix="__section-download-buttons"}}>
            <Download
              @disabled={{or0 isDateRangeInvalid (not (or0 this.output.id this.data.length))}}
              @filename={{queriedTitle}}
              @outputId={{this.output.id}}
              @isModeled={{this.isModeled}}
              @imageDisabled={{not this.data.length}}
              @onSelectImage={{graphDownload.onSelection}}
            />
            <ViewApiRequest
              @loader={{this.loader}}
              @filename={{queriedTitle}}
              @outputId={{this.output.id}}
              @isModeled={{this.isModeled}}
            />
          </div>
        </div>
        <div class={{classMod config=classModCfg suffix="__section-graph"}}>
          <div class={{classMod config=classModCfg suffix="__section-graph-actions"}}>
            <div class={{classMod config=classModCfg suffix="__section-graph-plot"}}>
              <Plot
                @isLoading={{this.isLoading}}
                @isLoaded={{this.isLoaded}}
                @isEmpty={{this.isEmpty}}
                @loader={{this.loader}}
                @disabled={{or0 isDateRangeInvalid this.isLoaded}}
                @onClickPlotButton={{fnx @onClickPlotButton @results noMoreArgs=true}}
              />
            </div>
            <div class="c-time-series-output__section-graph-loading">
              {{#if isDateRangeUnfilled}}
                <div class="c-error qa-error-dates qa-missing-dates">
                  Input date range on {{string "time-series-selection-route"}} tab
                </div>
              {{else if isDateRangeUnbound}}
                <div class="c-error qa-error-dates qa-outside-dates">
                  Dates are outside the available date range
                </div>
              {{else if this.isEmpty}}
                <div class="c-error qa-error-dates qa-ts-no-data">
                  No data available for the selected date range
                </div>
              {{else if (and
                (not this.isLoaded)
                (not this.isLoading)
                (not this.loader.loadedPages)
              )}}
                {{! Press Plot }}
              {{else if (not this.isLoaded)}}
                {{#if (not this.isLoading)}}
                  Paused
                {{else if this.loader.error}}
                  Error
                {{else if this.isLoading}}
                  Loading...
                {{/if}}
                {{#if this.loader.loadedPages}}
                  ({{this.loader.loadedPages}}/{{this.loader.totalPages}})
                {{/if}}
              {{/if}}
            </div>

            {{#if (or0 this.tooltips.length this.secondaryTooltips.length)}}
              {{#let
                (get this.tooltips 0)
                (get this.secondaryTooltips 0)
                as | tooltip secondaryTooltip |
              }}
              <div class="c-time-series-output__section-graph-tooltips">

                <div class="c-time-series-output__section-graph-tooltip-unpin">
                  {{#if secondaryTooltip}}
                    <CButton @type="small muted"
                      @title="Click to unpin current node details"
                      @action={{this.graph.unpin}}
                    >
                      <CIcon @icon="unpin" />
                      Unpin
                    </CButton>
                  {{/if}}
                </div>

                {{#if secondaryTooltip}}
                  <div class="c-time-series-output__section-graph-tooltip-pinned">
                    <span>{{dateTime secondaryTooltip.date}}</span> |
                    <span>
                      {{#if this.hasMultipleDataKeys}}
                        See values in legend
                      {{else}}
                        {{if (isUnset secondaryTooltip.value) "null" (
                          concat (localeNum secondaryTooltip.value)
                            " " (parameterUnit this.parameterUnit)
                        )}}
                      {{/if}}
                    </span>
                  </div>
                {{/if}}

                {{#if tooltip}}
                  <div class="c-time-series-output__section-graph-tooltip is-non-touch-only">
                  <span>{{dateTime tooltip.date}}</span>
                    |
                    <span>
                      {{#if this.hasMultipleDataKeys}}
                        See values in legend
                      {{else}}
                        {{if (isUnset tooltip.value) "null"
                          (concat (localeNum tooltip.value)
                            " " (parameterUnit this.parameterUnit)
                        )}}
                      {{/if}}
                    </span>
                  </div>
                {{/if}}

              </div>
              {{/let}}
            {{/if}}

          </div>
          <GraphView
            class={{classMod config=classModCfg suffix="__section-graph-output"}}
            @data={{this.data}}
            @paginated={{true}}
            @dataKey="modelRunMemberId"
            @dataKeyColors={{this.dataKeyColors}}
            @dataKeyLabels={{this.dataKeyLabels}}
            @dataKeyIndex={{this.dataKeyIndex}}
            @dataKeyCount={{this.dataKeyCount}}
            @yLabel={{concat this.parameterName (if this.parameterUnit (concat " (" this.parameterUnit ")"))}}
            @yKey="value"
            @xLabel={{scaleHasTimeFn "Date and Time (UTC)" "Date"}}
            @xKey="dateTime"
            @xTickAngle={{-25}}
            @tickArguments={{this.tickArguments}}
            @tickFormat={{this.tickFormat}}
            @tickMaxFormat={{this.tickMaxFormat}}
            @xLowerDomain={{qpDateObject this.queriedFrom}}
            @xUpperDomain={{qpDateObject this.effectiveQueriedTo}}
            @xLowerWindow={{or0 this.windowStart (qpDateObject this.queriedFrom)}}
            @xUpperWindow={{or0 this.windowEnd (qpDateObject this.effectiveQueriedTo)}}
            @fontSize={{12}}
            @backgroundColor="white"
            @onDomain={{this.onDomain}}
            @cursorType="vertical"
            @onTooltipChange={{this.onTooltipChange}}
            @tooltipCanvasWidth={{cssVar "--c-color___width"}}
            @tooltipCanvasHeight={{cssVar "--c-color___height"}}
            @tooltipAllowedClicks="c-time-series-output__legend-item"
            @lineMeta={{mrmMeta}}
            @interface={{this.graph}}
            @xWindowStart={{this.windowStart}}
            @xWindowEnd={{this.windowEnd}}
            @onGraphRectChange={{this.onGraphRectChange}}
            @marginRight={{this.graphScrollHandleWidth}}
            @lineColor={{lineColor}}
            @onInit={{@onGraphInit}}
            @onDestroy={{@onGraphDestroy}}
            @onInteract={{@onGraphInteract}}
            @headings={{if this.debugOutput.showHeaderInGraphView graphHeadings}}
            @footers={{if this.debugOutput.showFooterInGraphView graphFooters}}
            @legendRight={{this.debugOutput.showLegendInGraphView}}
          />
          <GraphScroll
            class={{classMod config=classModCfg suffix="__section-graph-output"}}
            @data={{this.data}}
            @paginated={{true}}
            @dataKey="modelRunMemberId"
            @dataKeyColors={{this.dataKeyColors}}
            @dataKeyLabels={{this.dataKeyLabels}}
            @dataKeyIndex={{this.dataKeyIndex}}
            @dataKeyCount={{this.dataKeyCount}}
            @yKey="value"
            @xLabel={{scaleHasTimeFn "Date and Time (UTC)" "Date"}}
            @xKey="dateTime"
            @xTickAngle={{-25}}
            @tickArguments={{this.tickArguments}}
            @tickFormat={{this.tickFormat}}
            @xLowerDomain={{qpDateObject this.queriedFrom}}
            @xUpperDomain={{qpDateObject this.effectiveQueriedTo}}
            @fontSize={{12}}
            @backgroundColor="white"
            @lineMeta={{mrmMeta}}
            @onWindowAnimate={{this.onWindowAnimate}}
            @onHandleSizeChange={{this.onGraphScrollHandleSizeChange}}
            @marginLeft={{this.graphScrollMarginLeft}}
            @lineColor={{lineColor}}
          />
          {{#if this.hasMultipleDataKeys}}
            {{#let
              (classModConfig (classMod ""
                this.graph.isMouseMove "--mouse"
                config=classModCfg
                suffix="__legend"
              ))
              (metaEvery mrmMeta "visible")
              as | legendCls isAllVisible |
            }}
            <div class={{classMod config=legendCls suffix="-title"}}>
              {{titleCase (if this.hasProfile "model-run-profile" "model-run-members")}}

              <div class={{classMod config=legendCls suffix="-actions"}}>

                <CButton @type="small muted"
                  class={{classMod config=legendCls suffix=(if isAllVisible "-show-all" "-hide-all")}}
                  @title={{concat "Click to " (if isAllVisible "hide" "show") " all "
                    (oneString this.hasProfile "model-run-profile" true "model-run") " lines"
                  }}
                  @action={{metaToggleAll mrmMeta "visible"}}
                >
                  <CIcon @icon={{if isAllVisible "hide" "show"}} />
                  {{if isAllVisible "Hide" "Show"}} All
                </CButton>

              </div>

            </div>

            <div class={{classMod config=legendCls}}>
              {{#each this.modelRunMembers as | modelRunMember index |}}
                {{#let
                  (metaGet mrmMeta index "visible")
                  (find (or0 this.tooltips this.secondaryTooltips) index "dataKeyIndex")
                  (find this.tooltips index "dataKeyIndex")
                  (find this.secondaryTooltips index "dataKeyIndex")
                  as | isVisible canvas tooltip pinned |
                }}
                <div class={{classMod config=legendCls suffix="-item"}}
                  {{on "click" (metaToggle mrmMeta index "visible")}} role="button"
                  title={{concat "Click to " (if isVisible "hide" "show") " line"}}
                >
                  <CColor
                    @colors={{this.dataKeyColors}}
                    @value={{value index this.dataKeyCount}}
                    @show={{isVisible}}
                    @hide={{not isVisible}}
                    @canvas={{canvas.canvas}}
                    class={{classMod config=legendCls suffix="-color"}}
                  />
                  <span class={{classMod config=legendCls suffix="-label"}}>
                    {{modelRunMember.description}}
                  </span>
                  <span class={{classMod config=legendCls suffix="-values"}}>
                    <LegendTooltip
                      @tooltip={{pinned}}
                      @parameterUnit={{this.parameterUnit}}
                    />
                    <LegendTooltip
                      class="is-non-touch-only"
                      @tooltip={{tooltip}}
                      @parameterUnit={{this.parameterUnit}}
                    />
                  </span>
                </div>
                {{/let}}
              {{/each}}
            </div>
            {{/let}}
            {{#if (not this.hasProfile)}}
              <div class={{classMod config=classModCfg suffix="__section-graph-footnote"}}>
                <p>
                  This model run has a multiple model run member (ensemble)
                  configuration, which provides multiple plausible scenarios of
                  future conditions. Each line on the plot is one model run
                  (ensemble) member. It is strongly recommended that run members be
                  considered collectively, rather than individually.
                </p>
              </div>
            {{/if}}
          {{/if}}
        </div>
      </div>
    {{~/let}}
    {{~/let}}
    {{~/let}}
    {{~/let}}
  </template>
}
